import React, { useState, useEffect, useRef } from 'react'
import { Popover, Spin, Checkbox, Input, Button } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import Axios from 'axios'
import { useDebounce } from 'Hooks'

const { REACT_APP_OL_API_USERNAME, REACT_APP_OL_API_PASSWORD } = process.env

interface Promotion {
  id: string
  name: string
  status: string
  type: string
  promotion_campaign_name: string
}

interface Props {
  selectedPromotions: string[]
  onPromotionSelect: (promotions: string[]) => void
  operator?: string
  disabled?: boolean
}

const PromotionSelectInput: React.FC<Props> = ({
  selectedPromotions,
  onPromotionSelect,
  operator,
  disabled,
}) => {
  const { REACT_APP_OL_API_PROMOTION_URL } = process.env
  const [promotions, setPromotions] = useState<Promotion[]>([])
  const [loading, setLoading] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const debouncedSearchValue = useDebounce(searchValue, 600)
  const [tempSelected, setTempSelected] = useState<string[]>([])
  const [dropdownVisible, setDropdownVisible] = useState(false)
  const searchInputRef = useRef<any>(null)
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
  })

  const fetchPromotions = async (page: number = 1, name: string = '') => {
    setLoading(true)
    try {
      const response = await Axios.get(
        `${REACT_APP_OL_API_PROMOTION_URL}/v1/promotions/externals/kariba/promotions`,
        {
          auth: {
            username: REACT_APP_OL_API_USERNAME || '',
            password: REACT_APP_OL_API_PASSWORD || '',
          },
          params: {
            order: 'created_at',
            direction: 'desc',
            status: 'running,waiting',
            page,
            name,
          },
        }
      )

      if (response.status === 200) {
        const newPromotions = response.data.promotions || []
        if (page === 1) {
          setPromotions(newPromotions)
        } else {
          // Avoid duplicates when appending
          setPromotions((prev) => {
            const existingIds = new Set(prev.map(p => p.id))
            const uniqueNew = newPromotions.filter(p => !existingIds.has(p.id))
            return [...prev, ...uniqueNew]
          })
        }
        setPagination(response.data.pagination || {
          current_page: page,
          last_page: 1,
          total: newPromotions.length
        })
      }
    } catch (error) {
      console.error('Error fetching promotions:', error)
    } finally {
      setLoading(false)
    }
  }

  // Load promotions when component mounts if we have selected ones
  useEffect(() => {
    if (selectedPromotions.length > 0 && promotions.length === 0) {
      // Load initial promotions to get details for selected items
      fetchPromotions(1, '')
    }
  }, [])

  useEffect(() => {
    if (dropdownVisible) {
      fetchPromotions(1, '')
    }
  }, [dropdownVisible])

  useEffect(() => {
    // Reset to page 1 when searching
    setPagination(prev => ({ ...prev, current_page: 1 }))
    fetchPromotions(1, debouncedSearchValue)
  }, [debouncedSearchValue])

  useEffect(() => {
    if (dropdownVisible) {
      setTempSelected(selectedPromotions)
      // If we have selected promotions but no promotion details loaded yet, fetch them
      if (selectedPromotions.length > 0 && promotions.length === 0) {
        fetchPromotions(1, '')
      }
    }
  }, [dropdownVisible, selectedPromotions])

  const handleSearch = (value: string) => {
    setSearchValue(value)
  }

  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { target } = e
    const element = target as HTMLDivElement

    // Check if scrolled near bottom (within 50px)
    if (element.scrollTop + element.offsetHeight >= element.scrollHeight - 50) {
      if (pagination.current_page < pagination.last_page && !loading) {
        fetchPromotions(pagination.current_page + 1, debouncedSearchValue)
      }
    }
  }

  const isSingleSelect = operator === 'EQUAL' || operator === 'NOT_EQUAL'

  const handleCheckboxChange = (promotionId: string, checked: boolean) => {
    if (isSingleSelect) {
      setTempSelected(checked ? [promotionId] : [])
    } else {
      if (checked) {
        setTempSelected([...tempSelected, promotionId])
      } else {
        setTempSelected(tempSelected.filter((id) => id !== promotionId))
      }
    }
  }

  const handleOk = () => {
    onPromotionSelect(tempSelected)
    setDropdownVisible(false)
  }

  const handleClear = () => {
    setTempSelected([])
  }

  const dropdownContent = () => (
    <div style={{ width: '600px' }}>
      <Input
        ref={searchInputRef}
        placeholder="Search..."
        prefix={<SearchOutlined />}
        value={searchValue}
        onChange={(e) => handleSearch(e.target.value)}
        style={{ marginBottom: 8 }}
      />

      <div
        style={{
          maxHeight: 400,
          overflowY: 'auto',
          border: '1px solid #f0f0f0',
          borderRadius: 4,
        }}
        onScroll={handlePopupScroll}
      >
        {/* Select All checkbox for multi-select */}
        {!isSingleSelect && promotions.length > 0 && (
          <div
            style={{
              padding: '10px 12px',
              borderBottom: '1px solid #f0f0f0',
              backgroundColor: '#fafafa',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Checkbox
              checked={promotions.length > 0 && tempSelected.length === promotions.length}
              indeterminate={tempSelected.length > 0 && tempSelected.length < promotions.length}
              onChange={(e) => {
                if (e.target.checked) {
                  setTempSelected(promotions.map((p) => p.id))
                } else {
                  setTempSelected([])
                }
              }}
              style={{ marginRight: 8 }}
            />
            <span style={{ fontWeight: 500 }}>Select All</span>
            {tempSelected.length > 0 && (
              <span style={{ marginLeft: 'auto', color: '#8c8c8c', fontSize: '12px' }}>
                {tempSelected.length} selected
              </span>
            )}
          </div>
        )}

        {loading && promotions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 30 }}>
            <Spin />
          </div>
        ) : promotions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>No promotions found</div>
        ) : (
          promotions.map((promotion) => (
            <div
              key={promotion.id}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'flex-start',
                backgroundColor: tempSelected.includes(promotion.id) ? '#e6f4ff' : '#fff',
                borderBottom: '1px solid #f0f0f0',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                if (!tempSelected.includes(promotion.id)) {
                  e.currentTarget.style.backgroundColor = '#fafafa'
                }
              }}
              onMouseLeave={(e) => {
                if (!tempSelected.includes(promotion.id)) {
                  e.currentTarget.style.backgroundColor = '#fff'
                }
              }}
              onClick={() => {
                handleCheckboxChange(promotion.id, !tempSelected.includes(promotion.id))
              }}
            >
              <Checkbox
                checked={tempSelected.includes(promotion.id)}
                style={{ marginRight: 8, marginTop: 2 }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', color: '#333' }}>{promotion.name}</div>
                {promotion.promotion_campaign_name && (
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    {promotion.promotion_campaign_name}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
        {loading && promotions.length > 0 && (
          <div style={{ textAlign: 'center', padding: 20, borderTop: '1px solid #f0f0f0' }}>
            <Spin size="small" />
            <div style={{ marginTop: 8, fontSize: '12px', color: '#999' }}>Loading more...</div>
          </div>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '12px 0',
          borderTop: '1px solid #f0f0f0',
        }}
      >
        <Button onClick={handleClear}>Clear All</Button>
        <Button type="primary" onClick={handleOk}>
          OK ({tempSelected.length} selected)
        </Button>
      </div>
    </div>
  )

  // Style constants similar to ProductSelectInput
  const STYLES = {
    selectedContainer: {
      base: {
        border: '1px solid #d9d9d9',
        borderRadius: '2px',
        padding: '8px',
        transition: 'all 0.3s',
      },
      enabled: {
        backgroundColor: '#fafafa',
        cursor: 'pointer',
        opacity: 1,
      },
      disabled: {
        backgroundColor: '#f5f5f5',
        cursor: 'not-allowed',
        opacity: 0.6,
      },
      hover: {
        borderColor: '#40a9ff',
        boxShadow: '0 0 0 2px rgba(24, 144, 255, 0.2)',
      },
      default: {
        borderColor: '#d9d9d9',
        boxShadow: 'none',
      },
    },
    countText: {
      marginBottom: '8px',
      color: '#8c8c8c',
      fontSize: '12px',
    },
    promotionItem: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '4px',
    },
    promotionName: {
      flex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
      fontSize: '12px',
      textAlign: 'left' as const,
    },
    moreText: {
      fontSize: '12px',
      color: '#8c8c8c',
      marginTop: '4px',
    },
  } as const

  // Get promotion details for selected items
  const getSelectedPromotionDetails = () => {
    return selectedPromotions.map((id) => {
      const promotion = promotions.find((p) => p.id === id)
      return promotion || { id, name: id, promotion_campaign_name: null }
    })
  }

  return (
    <Popover
      content={dropdownContent()}
      trigger={disabled ? '' : 'click'}
      visible={disabled ? false : dropdownVisible}
      onVisibleChange={disabled ? () => {} : setDropdownVisible}
      placement="bottom"
      overlayStyle={{ width: 600 }}
    >
      {selectedPromotions.length > 0 ? (
        <div
          style={{
            ...STYLES.selectedContainer.base,
            ...(disabled ? STYLES.selectedContainer.disabled : STYLES.selectedContainer.enabled),
          }}
          onMouseEnter={(e) => {
            if (!disabled) {
              Object.assign(e.currentTarget.style, STYLES.selectedContainer.hover)
            }
          }}
          onMouseLeave={(e) => {
            if (!disabled) {
              Object.assign(e.currentTarget.style, STYLES.selectedContainer.default)
            }
          }}
        >
          <div style={STYLES.countText}>{selectedPromotions.length} selected</div>
          {getSelectedPromotionDetails()
            .slice(0, 3)
            .map((promotion, index) => (
              <div
                key={promotion.id}
                style={{
                  ...STYLES.promotionItem,
                  marginBottom: index < 2 ? '4px' : 0,
                }}
              >
                <div style={STYLES.promotionName}>{promotion.name}</div>
              </div>
            ))}
          {selectedPromotions.length > 3 && (
            <div style={STYLES.moreText}>and {selectedPromotions.length - 3} more...</div>
          )}
        </div>
      ) : (
        <Input
          placeholder={disabled ? 'Please select operator first' : 'Select promotion...'}
          readOnly
          disabled={disabled}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        />
      )}
    </Popover>
  )
}

export default PromotionSelectInput
